import { computed, nextTick, ref, watch } from 'vue';
import { DialogQueuePriority } from '@/core/dialogsystem/dialogQueue';
import { useDialog } from '@/core/dialogsystem/useDialog';
import { useCurrentTenant } from '@/composables/schoolAdmin/useCurrentTenant';
import { useCurrentClass } from '@/schoolClasses/useCurrentClass';
import { usePermissions } from '@/composables/usePermissions';
import { useWhoAmI } from '@/composables/useWhoAmI';
import { useRouterHelper } from '@/composables/useRouterHelper';
import { fetchAvailableSchoolSubjectsForSchoolLevel } from '@/api/resources/schoolSubject.api';
import { useRouteContext } from '@/composables/useRouteContext';
import { useGrowthCta } from './useGrowthCta';
import { useDemoClassTour } from './useDemoClassTour';
import { useGrowthReadiness } from './useGrowthReadiness';
import GrowthCtaDialog from '@/core/dialogs/growth-cta/GrowthCtaDialog.vue';
import CreateClassDialog from '@/core/dialogs/create-class/CreateClassDialog.vue';
import { ECreateClassMode } from 'common/enum/misc.enum';
import { Components } from '@/api/generated/api';
import { useQuickEntryDialog } from '@/composables/quickEntry/useQuickEntryDialog';

const PAUSE_BEFORE_NEXT_STEP_MS = 700;

interface IOwnerActivationEntryOptions {
  allowTrialUpsellAfterEntry?: boolean;
}

const isActivationActive = ref(false);
const onSuccessSecondaryHandler = ref<(() => Promise<void> | void) | null>(null);
const onSuccessDismissHandler = ref<(() => Promise<void> | void) | null>(null);
const suppressedPopups = ref<Components.Schemas.GrowthPopupType[]>([]);

const suppressPopup = (popup: Components.Schemas.GrowthPopupType): void => {
  if (!suppressedPopups.value.includes(popup)) {
    suppressedPopups.value = [...suppressedPopups.value, popup];
  }
};

export const resetOwnerActivationChannel = (): void => {
  isActivationActive.value = false;
  onSuccessSecondaryHandler.value = null;
  onSuccessDismissHandler.value = null;
  suppressedPopups.value = [];
};

/**
 * Module-level state that lets other composables (e.g. useFirstEntrySuccessActions)
 * know that the owner-activation flow is driving the current session and hook
 * into the done / dismiss outcomes of the success dialog.
 */
export function useOwnerActivationChannel() {
  return {
    isActivationActive: computed(() => isActivationActive.value),
    onSuccessSecondary: computed(() => onSuccessSecondaryHandler.value),
    onSuccessDismiss: computed(() => onSuccessDismissHandler.value),
    suppressedPopups: computed(() => suppressedPopups.value),
  };
}

export function useOwnerActivationFlow(): void {
  const { currentTenant } = useCurrentTenant();
  const { currentClass, setCurrentClass } = useCurrentClass();
  const { hasTenantOwnerOrAdminAccess } = usePermissions();
  const { whoAmI } = useWhoAmI();
  const { popup, dismissPopup, canShowGrowthPopups } = useGrowthCta();
  const { navigateToAllStudents, pushThroughError } = useRouterHelper();
  const { isClassContext, isSchoolContext } = useRouteContext();
  const { isTourActive } = useDemoClassTour();
  const { isGrowthReady } = useGrowthReadiness();
  const { showQuickEntryDialogQueued } = useQuickEntryDialog();

  const hasFinishedThisSession = ref(false);

  const shouldStart = computed<boolean>(() => {
    if (!isGrowthReady.value || !canShowGrowthPopups.value) return false;
    if (hasFinishedThisSession.value || isActivationActive.value) return false;
    if (!currentTenant.value || !whoAmI.value) return false;
    if (currentTenant.value.isAdministrationSetupCompleted) return false;
    if (!hasTenantOwnerOrAdminAccess.value) return false;
    if (!currentClass.value?.isDemoClass) return false;
    if (isSchoolContext.value) return false;
    if (isTourActive.value) return false;
    if (suppressedPopups.value.includes('POPUP_FIRST_ENTRY')) return false;
    return popup.value === 'POPUP_FIRST_ENTRY';
  });

  const finish = (): void => {
    hasFinishedThisSession.value = true;
    isActivationActive.value = false;
    onSuccessSecondaryHandler.value = null;
    onSuccessDismissHandler.value = null;
  };

  const canContinue = (): boolean => canShowGrowthPopups.value && isActivationActive.value;

  const navigateToAdministrationSetup = async (): Promise<void> => {
    if (currentTenant.value) {
      await pushThroughError({
        name: '//school/[schoolId]/settings/',
        params: { schoolId: currentTenant.value.id },
      });
    }
  };

  const finishAfterDismiss = (): void => {
    suppressPopup('POPUP_FIRST_ENTRY');
    suppressPopup('POPUP_CREATE_CLASS');
    finish();
  };

  const hasAvailableSubjectsForNewClass = async (): Promise<boolean> => {
    const levels = currentTenant.value?.schoolLevels ?? [];
    for (const level of levels) {
      const subjects = await fetchAvailableSchoolSubjectsForSchoolLevel({ schoolLevel: level });
      if (subjects.length > 0) return true;
    }
    return false;
  };

  const showOwnerActivationEntry = (
    schoolClass: Components.Schemas.SchoolClassDto,
    options: IOwnerActivationEntryOptions = {},
  ): void => {
    void showQuickEntryDialogQueued(
      {
        dedupeKey: `owner-activation:entry:${schoolClass.id}`,
        guard: canContinue,
        onSkipped: finish,
        priority: DialogQueuePriority.OwnerActivation,
      },
      {
        preselectDefaultPerformanceAssessment: true,
        preferVisualPerformanceAssessment: schoolClass.isDemoClass,
        growthFlow: {
          schoolClassId: schoolClass.id,
          allowTrialUpsellAfterEntry: options.allowTrialUpsellAfterEntry,
        },
      },
    );
  };

  const createClassDialog = useDialog({
    component: CreateClassDialog,
    props: computed(() => ({
      onCreated: async (payload: { schoolClass: Components.Schemas.SchoolClassDto; mode: ECreateClassMode }) => {
        setCurrentClass(payload.schoolClass);
        if (!isClassContext.value) {
          await navigateToAllStudents();
        }
        await nextTick();
        suppressPopup('POPUP_CREATE_CLASS');
        suppressPopup('POPUP_SCHOOL_SETUP');
        onSuccessSecondaryHandler.value = async () => finish();
        onSuccessDismissHandler.value = async () => finish();
        setTimeout(() => {
          showOwnerActivationEntry(payload.schoolClass, {
            allowTrialUpsellAfterEntry: !payload.schoolClass.isDemoClass,
          });
        }, PAUSE_BEFORE_NEXT_STEP_MS);
      },
    })),
  });

  const openCreateClass = async (): Promise<void> => {
    const hasSubjects = await hasAvailableSubjectsForNewClass();
    if (!hasSubjects) {
      finish();
      await navigateToAdministrationSetup();
      return;
    }
    void createClassDialog.showQueued(
      {
        dedupeKey: 'owner-activation:create-class',
        guard: canContinue,
        onSkipped: finish,
        priority: DialogQueuePriority.OwnerActivation,
      },
      { mode: ECreateClassMode.MINIMAL },
    );
  };

  const openSchoolSetup = async (): Promise<void> => {
    finish();
    await navigateToAdministrationSetup();
  };

  const dismissSchoolSetupPrompt = async (): Promise<void> => {
    await dismissPopup('POPUP_SCHOOL_SETUP');
    suppressPopup('POPUP_SCHOOL_SETUP');
    finish();
  };

  /**
   * Shown after the user declined the 1st entry prompt.
   * Without available subject templates, the same step becomes the school setup
   * prompt instead of leaving the activation flow without an actionable next step.
   *
   * Scheduled via setTimeout (not awaited) so the first-entry dialog can close
   * first, otherwise the delay runs while the dialog is still visible.
   */
  const showCreateClassOrSchoolSetupPrompt = async (showCreateClassPrompt: () => void): Promise<void> => {
    if (await hasAvailableSubjectsForNewClass()) {
      showCreateClassPrompt();
      return;
    }

    void schoolSetupAfterCreateClassStepDialog.showQueued({
      dedupeKey: 'owner-activation:school-setup',
      guard: canContinue,
      onSkipped: finish,
      priority: DialogQueuePriority.OwnerActivation,
    });
  };

  const scheduleCreateClassPromptAfterDecline = (): void => {
    setTimeout(() => {
      void showCreateClassOrSchoolSetupPrompt(() => {
        void createClassAfterDeclineDialog.showQueued({
          dedupeKey: 'owner-activation:create-class-after-decline',
          guard: canContinue,
          onSkipped: finish,
          priority: DialogQueuePriority.OwnerActivation,
        });
      });
    }, PAUSE_BEFORE_NEXT_STEP_MS);
  };

  const createClassAfterDeclineDialog = useDialog({
    component: GrowthCtaDialog,
    props: computed(() => ({
      popup: 'POPUP_CREATE_CLASS' as Components.Schemas.GrowthPopupType,
      onPrimaryCreateClass: async () => {
        await dismissPopup('POPUP_CREATE_CLASS');
        await openCreateClass();
      },
      onSecondary: async () => {
        await dismissPopup('POPUP_CREATE_CLASS');
        finishAfterDismiss();
      },
      onDismiss: async () => {
        await dismissPopup('POPUP_CREATE_CLASS');
        finishAfterDismiss();
      },
    })),
  });

  /**
   * Shown after 1st (or 2nd) entry + "Fertig" path.
   * Declining here finishes the popup flow so the setup checklist can take over.
   */
  const createClassAfterEntryDialog = useDialog({
    component: GrowthCtaDialog,
    props: computed(() => ({
      popup: 'POPUP_CREATE_CLASS' as Components.Schemas.GrowthPopupType,
      onPrimaryCreateClass: async () => {
        await dismissPopup('POPUP_CREATE_CLASS');
        await openCreateClass();
      },
      onSecondary: async () => {
        await dismissPopup('POPUP_CREATE_CLASS');
        finishAfterDismiss();
      },
      onDismiss: async () => {
        await dismissPopup('POPUP_CREATE_CLASS');
        finishAfterDismiss();
      },
    })),
  });

  const schoolSetupAfterCreateClassStepDialog = useDialog({
    component: GrowthCtaDialog,
    props: computed(() => ({
      popup: 'POPUP_SCHOOL_SETUP' as Components.Schemas.GrowthPopupType,
      onPrimarySchoolSetup: openSchoolSetup,
      onSecondary: dismissSchoolSetupPrompt,
      onDismiss: dismissSchoolSetupPrompt,
    })),
  });

  const installSuccessHandlers = (): void => {
    onSuccessSecondaryHandler.value = () => {
      setTimeout(() => {
        void showCreateClassOrSchoolSetupPrompt(() => {
          void createClassAfterEntryDialog.showQueued({
            dedupeKey: 'owner-activation:create-class-after-entry',
            guard: canContinue,
            onSkipped: finish,
            priority: DialogQueuePriority.OwnerActivation,
          });
        });
      }, PAUSE_BEFORE_NEXT_STEP_MS);
    };
    onSuccessDismissHandler.value = () => {
      finishAfterDismiss();
    };
  };

  const firstEntryDialog = useDialog({
    component: GrowthCtaDialog,
    props: computed(() => ({
      popup: 'POPUP_FIRST_ENTRY' as Components.Schemas.GrowthPopupType,
      showFirstEntryPrimary: true,
      onPrimaryFirstEntry: async () => {
        if (!currentClass.value?.isDemoClass) return;
        const demoClass = currentClass.value;
        installSuccessHandlers();
        if (!isClassContext.value) {
          setCurrentClass(demoClass);
          await navigateToAllStudents();
          await nextTick();
        }
        showOwnerActivationEntry(demoClass);
      },
      onSecondary: async () => {
        await dismissPopup('POPUP_FIRST_ENTRY');
        scheduleCreateClassPromptAfterDecline();
      },
      onDismiss: async () => {
        await dismissPopup('POPUP_FIRST_ENTRY');
        finishAfterDismiss();
      },
    })),
  });

  const start = (): void => {
    if (isActivationActive.value) return;
    isActivationActive.value = true;
    void firstEntryDialog.showQueued({
      dedupeKey: 'owner-activation:first-entry',
      guard: () =>
        canContinue() &&
        popup.value === 'POPUP_FIRST_ENTRY' &&
        !!currentClass.value?.isDemoClass &&
        !isSchoolContext.value &&
        !isTourActive.value,
      onSkipped: finish,
      priority: DialogQueuePriority.OwnerActivation,
    });
  };

  watch(
    shouldStart,
    (next) => {
      if (next) start();
    },
    { immediate: true },
  );
}
